import { CampaignResponse, Conditions, Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import {
    ApplyContext,
    ConflictItem,
    FetchContext,
    FetchParams,
    OrderContext,
    SubmitError,
    ValidationError,
} from '../types';
import { CampaignStrategy } from './campaign-strategy';

const CONDITION_FIELD = 'QUANTIDADE_ITEM';
const PAYMENT_CONDITION_BONIFICACAO = '100';

const getConditionItems = (campaign: CampaignResponse): Conditions[] =>
    campaign.condicoes.filter((c) => c.campo === CONDITION_FIELD);

interface MultiplierResult {
    found: boolean;
    multiplier: number;
}

// Para cada condição QUANTIDADE_ITEM, calcula floor(qty / valor) e retorna o
// MÍNIMO entre as condições — espelha o comportamento do antigo `updateItemApi`
// e elimina a divergência do antigo `updateCampaignItems` (que sobrescrevia com
// o último item via .reduce()).
const computeMultiplier = (
    campaign: CampaignResponse,
    saleItems: ItemResponse[],
): MultiplierResult => {
    const conditions = getConditionItems(campaign);
    if (conditions.length === 0) return { found: false, multiplier: 0 };

    let min = Number.POSITIVE_INFINITY;
    let any = false;
    for (const cond of conditions) {
        const item = saleItems.find(
            (i) => i.number?.toLowerCase() === cond.numeroItem?.toLowerCase(),
        );
        if (!item) continue;
        const valor = Number(cond.valor) || 0;
        if (valor <= 0) continue;
        const qty = Number(item.quantity) || 0;
        const sum = Math.floor(qty / valor);
        if (sum < min) min = sum;
        any = true;
    }
    if (!any) return { found: false, multiplier: 0 };
    return { found: true, multiplier: Math.max(0, min) };
};

export class BonificacaoProporcionalStrategy implements CampaignStrategy {
    readonly tipo = 'BONIFICACAO_PROPORCIONAL' as const;

    isAvailableForOrder(
        _campaign: CampaignResponse,
        _context: OrderContext,
    ): boolean {
        return true;
    }

    validateEligibility(
        campaign: CampaignResponse,
        availableItems: ItemResponse[],
    ): ValidationError[] {
        const errors: ValidationError[] = [];
        for (const cond of getConditionItems(campaign)) {
            const item = availableItems.find(
                (i) => i.number === cond.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'CONDITION_ITEM_MISSING',
                    message: `SKU ${cond.numeroItem} (condição) não disponível`,
                    numeroItem: cond.numeroItem,
                });
            }
        }
        return errors;
    }

    detectConflicts(): ConflictItem[] {
        return [];
    }

    onEnable(
        _campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        return orderItems;
    }

    applyResultToItem(
        item: ItemResponse,
        result: Result,
        campaign: CampaignResponse,
        _context: ApplyContext,
    ): ItemResponse {
        // Preserva qty existente (ex.: bonificação previamente salva carregada
        // via tempItems) e cai pro base se for novo. `recalculateOnQuantityChange`
        // assume a partir daqui.
        const currentQty = Number(item.quantity) || 0;
        const baseQtd = Number(result.qtd) || 0;
        return {
            ...item,
            campaign,
            result,
            disabled: true,
            condition: undefined,
            quantity: currentQty > 0 ? currentQty : baseQtd,
        };
    }

    recalculateOnQuantityChange(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        context: ApplyContext,
    ): ItemResponse[] {
        // Condições vivem na tela de venda (paymentConditionId !== '100'); os
        // resultados aparecem na tela de bonificação. `saleItems` carrega os
        // itens da venda quando o caller está na bonificação. Em outros casos,
        // `orderItems` já contém tudo.
        const scopeForConditions = context.saleItems ?? orderItems;
        const { found, multiplier } = computeMultiplier(campaign, scopeForConditions);

        const resultByNumero = new Map<string, Result>();
        for (const r of campaign.resultados) {
            if (!r.numeroItem) continue;
            resultByNumero.set(r.numeroItem.toLowerCase(), r);
        }

        return orderItems.map((item) => {
            const result = resultByNumero.get((item.number ?? '').toLowerCase());
            if (!result) return item;
            if (item.campaign && item.campaign.id !== campaign.id) return item;

            // Sem condição visível (saleItems pode estar indisponível em F5 direto
            // na URL de bonificação): preserva o item — não temos info pra recalcular.
            // Quando `saleItems` está populado e condicao tem qty 0/missing, `found`
            // continua true se houver pelo menos uma condicao válida com qty>0; ou
            // false e respeitamos o estado anterior.
            if (!found) return item;

            const baseQtd = Number(result.qtd) || 0;
            const newQty = multiplier * baseQtd;

            // Edição manual do resultado: usuário pode bonificar ACIMA da proporção,
            // mas nunca abaixo. Clampa em newQty como mínimo. Preserva a customização
            // manual mesmo quando o recálculo é disparado por outro item (ex.: mudança
            // de item de condição/venda) — o Math.max garante que a qty do usuário
            // sobreviva a recalcs indiretos.
            const userQty = Number(item.quantity) || 0;
            const finalQty = Math.max(userQty, newQty);

            if (item.quantity === finalQty && item.max === newQty) return item;
            return {
                ...item,
                quantity: finalQty,
                max: newQty,
            };
        });
    }

    validateForSubmit(): SubmitError[] {
        return [];
    }

    requiresExtraFetchParams(
        _campaign: CampaignResponse,
        _context: FetchContext,
    ): Partial<FetchParams> {
        return {};
    }

    acceptsPaymentCondition(paymentConditionId: string): boolean {
        return paymentConditionId === PAYMENT_CONDITION_BONIFICACAO;
    }
}

export const computeProporcionalMultiplier = computeMultiplier;
export const getProporcionalConditionItems = getConditionItems;
